import type { Middleware } from '@reduxjs/toolkit';
import { isRejectedWithValue, isFulfilled, isPending } from '@reduxjs/toolkit';
import { telemetry } from '@/services/telemetry';

interface RtkQueryArg {
  endpointName?: string;
  type?: string;
  startedTimeStamp?: number;
}

const pendingTimestamps = new Map<string, number>();

function classifyError(
  payload: Record<string, unknown> | undefined
): 'network' | 'validation' | 'server' | 'unknown' {
  if (!payload) return 'unknown';
  const status = typeof payload.status === 'number' ? payload.status : undefined;
  if (status === undefined || status === 0) return 'network';
  if (status >= 400 && status < 500) return 'validation';
  if (status >= 500) return 'server';
  return 'unknown';
}

export const telemetryMiddleware: Middleware = () => (next) => (action) => {
  const result = next(action);

  if (isPending(action)) {
    const arg = action.meta?.arg as RtkQueryArg | undefined;
    if (arg?.endpointName) {
      pendingTimestamps.set(String(arg.endpointName), Date.now());
    }
  }

  if (isFulfilled(action)) {
    const arg = action.meta?.arg as RtkQueryArg | undefined;
    if (arg?.endpointName) {
      const endpoint = String(arg.endpointName);
      const method = arg.type === 'mutation' ? 'POST' : 'GET';
      const startTime = pendingTimestamps.get(endpoint) ?? Date.now();
      const durationMs = Date.now() - startTime;
      pendingTimestamps.delete(endpoint);

      telemetry.trackDependency(endpoint, method, 200, durationMs, true, {
        status: 'Success',
      });
    }
  }

  if (isRejectedWithValue(action)) {
    const arg = action.meta?.arg as RtkQueryArg | undefined;
    if (arg?.endpointName) {
      const endpoint = String(arg.endpointName);
      const method = arg.type === 'mutation' ? 'POST' : 'GET';
      const startTime = pendingTimestamps.get(endpoint) ?? Date.now();
      const durationMs = Date.now() - startTime;
      pendingTimestamps.delete(endpoint);

      const payload = action.payload as Record<string, unknown> | undefined;
      const statusCode = typeof payload?.status === 'number' ? payload.status : 0;
      const errorCategory = classifyError(payload);
      const errorMessage =
        typeof payload?.status === 'string' || typeof payload?.status === 'number'
          ? String(payload.status)
          : 'Unknown';

      telemetry.trackDependency(endpoint, method, statusCode, durationMs, false, {
        status: 'Failure',
        error: errorMessage,
        errorCategory,
      });
    }
  }

  return result;
};
